import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import {
  HardHat,
  Loader2,
  CheckCircle2,
  ShieldAlert,
  Sparkles,
  Mail,
  UserRound,
} from "lucide-react";

/**
 * Public "Be my accountant" onboarding page.
 * Reached by scanning the QR from Settings: /onboard-accountant?u=<uid>&t=<token>
 *
 * Flow:
 *   1. On mount, hit GET /api/accountant/invite/info to (a) validate the
 *      token and (b) show the accountant who invited them.
 *   2. Accountant enters their name + email → POST /api/accountant/invite/accept.
 *   3. Success screen — accountant closes the tab and the tradie's profile
 *      is instantly prefilled (BAS / TPAR / Pack email buttons now target
 *      this accountant).
 */
export default function OnboardAccountant() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const u = params.get("u") || "";
  const t = params.get("t") || "";

  const [state, setState] = useState("loading"); // 'loading' | 'ready' | 'invalid' | 'done'
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!u || !t) {
      setState("invalid");
      setErrorMsg("This invite link is incomplete. Ask your tradie to send it again.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/accountant/invite/info?u=${encodeURIComponent(u)}&t=${encodeURIComponent(t)}`);
        setInfo(data);
        setState("ready");
      } catch (err) {
        devLog.warn("invite info failed", err);
        setState("invalid");
        setErrorMsg(err?.response?.data?.detail || "This invite link is no longer valid.");
      }
    })();
  }, [u, t]);

  const submit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/accountant/invite/accept?u=${encodeURIComponent(u)}&t=${encodeURIComponent(t)}`,
        { name: name.trim(), email: email.trim() }
      );
      setState("done");
    } catch (err) {
      devLog.warn("invite accept failed", err);
      toast.error(err?.response?.data?.detail || "Could not save — try again");
    } finally {
      setBusy(false);
    }
  }, [u, t, name, email]);

  const tradieLabel =
    info?.business_name?.trim() ||
    info?.tradie_name?.trim() ||
    "the tradie";

  return (
    <div className="min-h-screen bi-grain bi-ambient text-white p-6">
      <div className="max-w-md mx-auto pt-12 space-y-6">
        <div className="flex items-center gap-2 justify-center" data-testid="onb-brand">
          <div className="w-10 h-10 rounded-xl bg-[#FF9500]/10 border border-[#FF9500]/30 flex items-center justify-center">
            <HardHat className="w-5 h-5 text-[#FF9500]" />
          </div>
          <div className="font-heading">
            <div className="text-lg font-extrabold leading-none">
              <span className="text-white">BUILDINTEL</span>
              <span className="text-[#FF9500]"> AI</span>
            </div>
            <div className="text-[10px] tracking-[0.25em] text-white/40 uppercase">Accounting in your pocket</div>
          </div>
        </div>

        {state === "loading" && (
          <div className="bi-surface p-6 text-center text-white/60 inline-flex items-center gap-2 justify-center w-full" data-testid="onb-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your invite…
          </div>
        )}

        {state === "invalid" && (
          <div className="bi-surface p-6 text-center space-y-3" data-testid="onb-invalid">
            <ShieldAlert className="w-8 h-8 text-[#FF9500] mx-auto" />
            <div className="font-heading font-bold text-lg">Invite unavailable</div>
            <div className="text-sm text-white/60">{errorMsg}</div>
          </div>
        )}

        {state === "ready" && info && (
          <form onSubmit={submit} className="bi-surface p-5 space-y-4" data-testid="onb-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-1 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-[#FF9500]" /> You&apos;re invited to accountant
              </div>
              <div className="font-heading text-xl font-extrabold text-white" data-testid="onb-tradie-name">
                {tradieLabel}
              </div>
              {info.trade_type && (
                <div className="text-xs text-white/50 mt-0.5">{info.trade_type}</div>
              )}
              {info.tradie_email && (
                <div className="text-[11px] text-white/40 mt-0.5">Contact: {info.tradie_email}</div>
              )}
            </div>

            <p className="text-[12px] text-white/60 leading-relaxed border-t border-white/5 pt-3">
              Enter your <b>name</b> and <b>email</b> and we&apos;ll save them on{" "}
              {tradieLabel}&apos;s BuildIntel profile — from then on, every BAS,
              TPAR and Accountant Pack Email button in their app will land
              straight in your inbox. Takes 20 seconds.
            </p>

            <div className="space-y-2.5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-1 inline-flex items-center gap-1"><UserRound className="w-3 h-3" /> Your full name</label>
                <input
                  type="text"
                  autoFocus
                  required
                  data-testid="onb-name"
                  className="w-full bi-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Sarah Nguyen"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-1 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Your work email</label>
                <input
                  type="email"
                  required
                  data-testid="onb-email"
                  className="w-full bi-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@firm.com.au"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="onb-submit"
              disabled={busy}
              className="w-full h-12 rounded-xl bi-accent-btn font-heading font-bold text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Save me as the accountant
            </button>

            <div className="text-[10px] text-white/40 text-center leading-snug">
              Your details are only saved to <b>{tradieLabel}</b>&apos;s profile. No
              BuildIntel account is created — you don&apos;t need to sign up.
            </div>
          </form>
        )}

        {state === "done" && (
          <div className="bi-surface p-6 text-center space-y-3" data-testid="onb-done">
            <CheckCircle2 className="w-10 h-10 text-[#FF9500] mx-auto" />
            <div className="font-heading font-bold text-lg">You&apos;re all set</div>
            <div className="text-sm text-white/60">
              {tradieLabel}&apos;s BuildIntel will now send BAS, TPAR and Accountant Pack summaries to <b>{email}</b>.
            </div>
            <button
              type="button"
              data-testid="onb-close"
              onClick={() => navigate("/")}
              className="mt-2 h-10 px-4 rounded-lg bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
